/**
 * ContentLink — CX-2764 "View in Imagio" / "Check it now" CTA.
 *
 * Shared link component rendered by material cards and sample rows across the
 * Circular CMF Guide chapters and the Design Trends detail pages.
 *
 * Behaviour is driven by `variant`:
 *   - "check-it-now": auth-gated in-app navigation to /search
 *   - "view-in-imagio": auth-gated; opens the Imagio search URL in a new tab
 *     (env-aware) once the visitor is logged in
 *
 * Both variants require login (CX-3744): an unauthenticated visitor gets the
 * login dialog instead of navigating or leaving to Imagio, and the intended
 * same-origin destination is stashed so it resumes in-app after login rather
 * than being lost (see usePostLoginRedirect). Both are hidden for
 * demo accounts, and "view-in-imagio" is also hidden when no valid target URL
 * can be resolved — prevents broken/dead links (CX-2764 AC: "the link is hidden
 * or disabled").
 */

import { UnauthenticationDialog } from "components/dialog/UnauthenticationDialog";
import { useAuthUser } from "hooks/authentication";
import type { CdcUserAttributes } from "models/cdcUser";
import { useUserInfoContext } from "providers/UserInfoProvider";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { STORAGE_KEYS, sessionStorageService } from "service/storage";
import { getDataFromLocalstorageByKey } from "service/utils/get-data-localstorage";
import type { ContentLinkProps } from "./ContentLink.types";
import "./ContentLink.css";

const DEFAULT_LABELS: Record<ContentLinkProps["variant"], string> = {
  "view-in-imagio": "View in Imagio®",
  "check-it-now": "Check it now",
};

// Reduce a candidate URL to an in-app (same-origin) path, or undefined if it is
// cross-origin / unparseable — react-router can only navigate same-origin.
const toSameOriginPath = (url: string): string | undefined => {
  // A protocol-relative URL ("//host/x") is NOT same-origin — exclude it so it
  // falls through to the origin check below rather than being treated as a path.
  if (url.startsWith("/") && !url.startsWith("//")) return url;
  try {
    const parsed = new URL(url);
    if (parsed.origin === window.location.origin) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  } catch {
    return undefined;
  }
  return undefined;
};

export const ContentLink = ({
  variant,
  searchUrl,
  productId,
  imagioUrl,
  label,
  className,
}: ContentLinkProps) => {
  const navigate = useNavigate();
  const [showAuthDialog, setShowAuthDialog] = useState(false);

  const { user } = useAuthUser();
  const cdcUser: CdcUserAttributes | undefined =
    getDataFromLocalstorageByKey("cdcUser") ?? undefined;
  const isAuthenticated = Boolean(user || cdcUser);

  const { userInfo } = useUserInfoContext();
  const isDemoAccount = userInfo?.accessLevel === "DEMO";

  const hasValidTarget =
    !isDemoAccount &&
    (variant === "view-in-imagio"
      ? Boolean(imagioUrl && imagioUrl !== "#")
      : true);

  const openImagio = () => {
    if (!imagioUrl || imagioUrl === "#") return;
    const resolvedUrl = imagioUrl.startsWith("/")
      ? `${window.location.origin}${imagioUrl}`
      : imagioUrl;
    window.open(resolvedUrl, "_blank", "noopener,noreferrer");
  };

  // Single source of the check-it-now target (searchUrl → productId query →
  // "/search" fallback) so navigation and post-login resume can't drift apart.
  const checkItNowTarget = (): string => {
    if (searchUrl) return searchUrl;
    if (productId) return `/search?productId=${productId}`;
    return "/search";
  };

  const navigateToSearch = () => {
    navigate(checkItNowTarget());
  };

  // The in-app destination to resume after login. Cross-origin Imagio URLs
  // cannot be reached via react-router, so they are not stashed (real data is
  // all same-origin /search deep links).
  const resumeTarget = (): string | undefined => {
    if (variant === "view-in-imagio") {
      if (!imagioUrl || imagioUrl === "#") return undefined;
      return toSameOriginPath(imagioUrl);
    }
    return checkItNowTarget();
  };

  const handleClick = () => {
    // CX-3744: both CTAs are auth-gated. An unauthenticated visitor gets the
    // login dialog; the intended destination is stashed so it resumes in-app
    // once login completes (usePostLoginRedirect), surviving the CDC/SSO reload.
    if (!isAuthenticated) {
      const target = resumeTarget();
      if (target) {
        sessionStorageService.setItem(STORAGE_KEYS.POST_LOGIN_REDIRECT, target);
      }
      setShowAuthDialog(true);
      return;
    }
    if (variant === "view-in-imagio") {
      openImagio();
      return;
    }
    navigateToSearch();
  };

  if (!hasValidTarget) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className={`content-link content-link--${variant}${className ? ` ${className}` : ""}`}
        onClick={handleClick}
      >
        <span className="content-link__label">
          {label ?? DEFAULT_LABELS[variant]}
        </span>
        <svg
          width="13"
          height="13"
          viewBox="0 0 14 14"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M3 7h8M7.5 3.5L11 7l-3.5 3.5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <UnauthenticationDialog
        isOpen={showAuthDialog}
        onClose={setShowAuthDialog}
      />
    </>
  );
};
